import { defineStore } from 'pinia'
import { toBigNumber } from '@injectivelabs/utils'
import { sharedGetToken } from '@shared/utils/helper'
import { injToken, injErc20Token } from '@shared/data/token'
import { tokenCacheApi, tokenStaticFactory } from '@shared/service'
import { TokenType, TokenVerification } from '@injectivelabs/sdk-ts/types'
import { web3Client } from '@/app/services/web3'
import { networkTokensMapping } from '@/app/data/network'
import { fetchErc20TokenCoinId } from '@/app/services/coingecko'
import { fetchAllLegacyWHTokenAddresses } from '@/app/services/wormhole/legacy/contract-tokens'
import {
  ethNativeToken,
  injForeignBaseDenomMap,
  injForeignBaseDenomChainIdMap
} from '@/app/data/token'
import type { Network } from '@shared/types'
import type { TokenStatic } from '@injectivelabs/sdk-ts'

type TokenStoreState = {
  tokenUsdPriceMap: Record<string, number>
  cw20TokenMap: Record<string, TokenStatic>
  bankTokenMap: Record<string, TokenStatic>
  peggyDenomTokenMap: Record<string, TokenStatic>
  wormholeDenomTokenMap: Record<string, TokenStatic>
  hardcodedDenomTokenMap: Record<string, TokenStatic>
}

const initialStateFactory = (): TokenStoreState => ({
  tokenUsdPriceMap: {} as Record<string, number>,
  cw20TokenMap: {} as Record<string, TokenStatic>,
  peggyDenomTokenMap: {} as Record<string, TokenStatic>,
  hardcodedDenomTokenMap: {} as Record<string, TokenStatic>,
  wormholeDenomTokenMap: {} as Record<string, TokenStatic>,
  bankTokenMap: {} as Record<string, TokenStatic>
})

export const useTokenStore = defineStore('token', {
  state: (): TokenStoreState => initialStateFactory(),
  getters: {
    ethereumNetworkTokens: (state) => {
      const sharedTokenStore = useSharedTokenStore()

      const tradablePeggyTokens = Object.values(
        sharedTokenStore.tradableDenomTokenMap
      ).filter(
        (token) =>
          token.denom.startsWith('peggy') || token.denom === injToken.denom
      )

      const peggyTokensFromBank = Object.values(state.peggyDenomTokenMap)

      const uniquePeggyTokens = [
        ...new Map(
          [...tradablePeggyTokens, ...peggyTokensFromBank].map((token) => [
            token.denom,
            token
          ])
        ).values()
      ]

      return [...uniquePeggyTokens]
    },

    injCosmosToken: (_) => (network: Network) => {
      const sharedTokenStore = useSharedTokenStore()

      const token = sharedTokenStore.tradableDenomTokenMap[injToken.denom]
      const baseDenom = injForeignBaseDenomMap[network]

      return {
        ...token,
        baseDenom
      } as TokenStatic
    },

    injCosmosTokenByChainId: (_) => (chainId: string) => {
      const sharedTokenStore = useSharedTokenStore()

      const token = sharedTokenStore.tradableDenomTokenMap[injToken.denom]
      const baseDenom = injForeignBaseDenomChainIdMap[chainId]

      return {
        ...token,
        baseDenom
      } as TokenStatic
    },

    tokenFromList: (_) => (denom: string) => {
      return tokenStaticFactory.toToken(denom)
    },

    tokenByDenomOrSymbol:
      (state) =>
      (denomOrSymbol?: string): undefined | TokenStatic => {
        if (!denomOrSymbol) {
          return
        }

        const sharedTokenStore = useSharedTokenStore()

        const tokens = {
          [injToken.denom]: injToken,
          [injErc20Token.denom]: injErc20Token,
          [ethNativeToken.denom]: ethNativeToken,
          ...sharedTokenStore.tradableDenomTokenMap,
          ...state.peggyDenomTokenMap,
          ...state.wormholeDenomTokenMap,
          ...state.hardcodedDenomTokenMap,
          ...state.bankTokenMap,
          ...state.cw20TokenMap
        }

        const matchingKey = Object.keys(tokens).find(
          (key) => key.toLowerCase() === denomOrSymbol.toLowerCase()
        )

        if (matchingKey) {
          return tokens[matchingKey]
        }

        return (
          tokens[denomOrSymbol] ||
          Object.values(tokens).find(
            ({ denom, symbol, baseDenom, address }) => {
              return [denom, symbol, address, baseDenom].includes(denomOrSymbol)
            }
          ) ||
          sharedTokenStore.tokenByDenomOrSymbol(denomOrSymbol)
        )
      }
  },
  actions: {
    fetchHardcodedTokens() {
      const tokenStore = useTokenStore()
      const hardcodedDenom = Object.entries(networkTokensMapping).reduce(
        (list, [_, denoms]) => [...list, ...denoms],
        [] as string[]
      )

      const hardcodedDenomTokens = [...new Set(hardcodedDenom)]
        .filter((denom) => denom)
        .map((denom) => {
          const token = tokenStaticFactory.toToken(denom)

          if (!token) {
            return undefined
          }

          return { [token.denom]: token }
        })
        .filter((denomMap) => !!denomMap)

      const hardcodedDenomTokenMap = hardcodedDenomTokens.reduce(
        (list, denomMap) => {
          if (!denomMap) {
            return list
          }

          return { ...list, ...denomMap }
        },
        {}
      ) as Record<string, TokenStatic>

      tokenStore.$patch({
        hardcodedDenomTokenMap: {
          ...tokenStore.hardcodedDenomTokenMap,
          ...hardcodedDenomTokenMap
        }
      })
    },

    async fetchWormholeTransferredTokens() {
      const bankStore = useBankStore()
      const tokenStore = useTokenStore()

      const factoryDenoms = bankStore.bankBalances
        .filter(({ denom }) => denom.startsWith('factory'))
        .map(({ denom }) => denom)
      const cw20Denoms = bankStore.cw20Balances.map(({ denom }) => denom)

      const whContractAddresses = await fetchAllLegacyWHTokenAddresses()
      const filteredDenoms = [...factoryDenoms, ...cw20Denoms].filter(
        (denom) => {
          const isFactory = denom.startsWith('factory')

          if (isFactory) {
            const [address] = denom.split('/').slice(-1)

            return address ? whContractAddresses.includes(address) : false
          }

          return whContractAddresses.includes(denom)
        }
      )

      const wormholeDenomTokenMap = Object.assign(
        {},
        ...(await Promise.all(
          filteredDenoms.map(async (denom) => {
            const token = await sharedGetToken(denom)

            return token ? { [token.denom]: token } : undefined
          })
        ))
      )

      tokenStore.$patch({
        wormholeDenomTokenMap: {
          ...tokenStore.wormholeDenomTokenMap,
          ...wormholeDenomTokenMap
        }
      })
    },

    async fetchBankTokens() {
      const bankStore = useBankStore()
      const tokenStore = useTokenStore()

      if (bankStore.bankBalances.length === 0) {
        return
      }

      const bankDenoms = bankStore.bankBalances
        .filter(
          ({ denom, amount }) =>
            toBigNumber(amount).gt(0) && !denom.startsWith('share')
        )
        .map(({ denom }) => denom)

      const bankTokenMap = Object.assign(
        {},
        ...(
          await Promise.all(
            [...bankDenoms].map(async (denom) => {
              const token = await sharedGetToken(denom)

              return token ? { [token.denom]: token } : undefined
            })
          )
        ).filter(Boolean)
      )

      tokenStore.$patch({
        bankTokenMap
      })
    },

    fetchCw20Tokens() {
      const bankStore = useBankStore()
      const tokenStore = useTokenStore()

      if (bankStore.bankBalances.length === 0) {
        return
      }

      const tokenMap = bankStore.cw20Balances.reduce(
        (list, balanceWithToken) => {
          const { amount, denom, token } = balanceWithToken

          if (toBigNumber(amount).isZero()) {
            return list
          }

          const tokenFromList = tokenStore.tokenFromList(denom)

          return { ...list, [denom]: (tokenFromList || token) as TokenStatic }
        },
        {} as Record<string, TokenStatic>
      )

      tokenStore.$patch({
        cw20TokenMap: tokenMap
      })
    },

    async fetchPeggyTokens() {
      const tokenStore = useTokenStore()
      const { supply } = await tokenCacheApi.fetchTotalSupply()

      const peggyDenoms = supply
        .map(({ denom }: any) => denom)
        .filter((denom: any) => denom.startsWith('peggy'))

      const peggyTokens = [...new Set(peggyDenoms)]
        .map((denom: any) => {
          const token = tokenStaticFactory.toToken(denom)

          if (
            !token ||
            token.tokenType === TokenType.Unknown ||
            token.tokenVerification !== TokenVerification.Verified
          ) {
            return undefined
          }

          return { [token.denom]: token }
        })
        .filter((denomMap) => !!denomMap)

      const peggyDenomTokenMap = peggyTokens.reduce((list, denomMap) => {
        if (!denomMap) {
          return list
        }

        return { ...list, ...denomMap }
      }, {}) as Record<string, TokenStatic>

      tokenStore.$patch({
        peggyDenomTokenMap: {
          ...tokenStore.peggyDenomTokenMap,
          ...peggyDenomTokenMap
        }
      })
    },

    async fetchCustomErc20TokenByAddress(
      tokenAddress: string
    ): Promise<undefined | TokenStatic> {
      const tokenStore = useTokenStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const denom = !tokenAddress.startsWith('0x')
        ? tokenAddress
        : `peggy${tokenAddress}`
      const token = await sharedGetToken(denom)

      const isEthToken =
        tokenAddress.startsWith('peggy') || tokenAddress.startsWith('0x')

      if (token) {
        const tokenWithCoinGeckoId = {
          ...token,
          ...(token.coinGeckoId || !isEthToken
            ? {}
            : {
                coinGeckoId: (await fetchErc20TokenCoinId(tokenAddress)) || ''
              })
        }

        const tokenExistInSupply = tokenStore.tokenByDenomOrSymbol(token.denom)

        if (!tokenExistInSupply) {
          tokenStore.$patch({
            hardcodedDenomTokenMap: {
              ...tokenStore.hardcodedDenomTokenMap,
              [token.denom]: tokenWithCoinGeckoId
            }
          })
        }

        return token
      }

      if (!isEthToken) {
        return
      }

      const tokenFromWeb3 = await web3Client.fetchTokenMetaData(tokenAddress)

      if (tokenFromWeb3) {
        const decimals = tokenFromWeb3.decimals || 18
        const token = {
          decimals,
          denom,
          address: tokenAddress,
          tokenType: TokenType.Erc20,
          name: tokenFromWeb3.name || '',
          logo: tokenFromWeb3.logo || '',
          symbol: tokenFromWeb3.symbol || '',
          coinGeckoId: (await fetchErc20TokenCoinId(tokenAddress)) || ''
        } as TokenStatic

        const tokenExistInSupply = tokenStore.tokenByDenomOrSymbol(token.denom)

        if (!tokenExistInSupply) {
          tokenStore.$patch({
            hardcodedDenomTokenMap: {
              ...tokenStore.hardcodedDenomTokenMap,
              [token.denom]: token
            }
          })
        }

        return token
      }
    },

    reset() {
      const tokenStore = useTokenStore()

      tokenStore.cw20TokenMap = {} as Record<string, TokenStatic>
      tokenStore.bankTokenMap = {} as Record<string, TokenStatic>
    }
  }
})
